import { ApiQueryName } from "@/models/api"
import { Currency } from "@/models/currency"
import type { HttpError } from "@/utils/api"
import { toHttpError } from "@/utils/api"
import { isSupportedCurrencyCode } from "@/utils/currency"
import { useQuery } from "@tanstack/react-query"
import type { CollectionProxy } from "spraypaint/lib-esm/proxies"

import type { SpraypaintQueryOptions, UseApiOptions } from "../../types"
import { useRenameQueryResult } from "../../utils"
import { createQueryKey } from "../../utils/createQueryKey"

/**
 * Gets currencies
 */
const getCurrencies = async ({
  extraFields,
  includes,
  page,
  pageSize,
  sort,
  where,
}: SpraypaintQueryOptions): Promise<Currency[]> => {
  try {
    const response: CollectionProxy<Currency> = await Currency.where(where)
      .includes(includes ?? [])
      .order(sort ?? { name: "asc" })
      .page(page ?? 1)
      .per(pageSize ?? 250)
      .selectExtra(extraFields ?? [])
      .all()

    return response.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

/**
 * Returns currencies
 *
 * @example
 * const {
 *   currenciesData,
 *   currenciesIsPending,
 *   currenciesError
 *  } = useCurrencies(options)
 */
export const useCurrencies = (
  options: UseApiOptions<SpraypaintQueryOptions, Currency[], HttpError> = {}
) => {
  const { apiQueryOptions = {}, queryOptions = {} } = options

  const queryKey = createQueryKey(ApiQueryName.Currencies, "getMany", null, {
    apiQueryOptions,
  })
  const queryResult = useQuery({
    gcTime: Infinity,
    staleTime: Infinity,
    ...queryOptions,
    queryFn: () => getCurrencies(apiQueryOptions),
    queryKey,
    select(data) {
      return data.filter((currency) =>
        isSupportedCurrencyCode(currency.alphaCode)
      )
    },
  })

  return useRenameQueryResult(queryResult, ApiQueryName.Currencies)
}
